import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';

import { useConfig, withConfig } from 'Context/ConfigContext';

import annotationPlugin from 'chartjs-plugin-annotation';
import 'dayjs/locale/th';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { compose } from 'recompose';
import styled, { useTheme } from 'styled-components';

import { Box, Flex, SvgIcon, Text } from 'Components/Base';
import { withOffset } from 'Context/LayoutContext';
import { getColorBySubjectName } from 'Util/myPath';

import { getCurrentWeekRange } from '../utils';

import {
  createChartData,
  getAllSubjects,
  getHighlightCurrentDayPlugin,
  getOptions,
  transformApiDataToChartData,
} from './utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);

const WeeklyStackedBarChartWrapper = styled(Flex)(({ theme }) => ({
  width: '100%',
  height: 'fit-content',
  maxWidth: '280px',
  flexDirection: 'column',
  gridRowGap: '12px',
}));

const WeeklyStackedBarChart = ({ data, studentSemesters, haveGoals }) => {
  const theme = useTheme();

  const { myPath } = useConfig();

  const highlightCurrentDayPlugin = useMemo(() => getHighlightCurrentDayPlugin(theme), [theme]);

  const chartData = useMemo(
    () => transformApiDataToChartData(data, studentSemesters, myPath.subjectList) ?? [],
    [data, studentSemesters, myPath.subjectList]
  );

  const allSubjects = useMemo(() => getAllSubjects(chartData), [chartData]);

  const barData = useMemo(
    () => createChartData(chartData, theme, haveGoals),
    [chartData, theme, haveGoals]
  );

  const options = useMemo(
    () => getOptions(chartData, haveGoals, theme),
    [chartData, haveGoals, theme]
  );

  return (
    <WeeklyStackedBarChartWrapper>
      <Text
        textStyle={'caption'}
        style={{ textAlign: 'center' }}
        color={'myPath.textColor.secondary'}
      >
        {getCurrentWeekRange()}
      </Text>
      <Box
        style={{
          height: '200px',
          width: '100%',
        }}
      >
        <Bar data={barData} options={options} plugins={[highlightCurrentDayPlugin]} />
      </Box>
      <Flex
        justifyContent={'center'}
        gridColumnGap={2}
        gridRowGap={2}
        flexWrap={'wrap'}
        style={{
          width: '100%',
        }}
      >
        {allSubjects.map((subject) => {
          const color = getColorBySubjectName(theme, subject);
          return (
            <Flex
              key={subject}
              flexDirection={'row'}
              flexWrap={'wrap'}
              gridColumnGap={1}
              alignItems={'center'}
            >
              <SvgIcon icon={SvgIcon.ICON_LIST.CIRCLE} size="12" color={color} />
              <Text textStyle={'xxs'} color={'myPath.textColor.primary'}>
                {subject}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </WeeklyStackedBarChartWrapper>
  );
};

WeeklyStackedBarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      subject: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      duration: PropTypes.number,
    })
  ),
  studentSemesters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      userId: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      actualStartDate: PropTypes.string,
      weekdayCapWorkload: PropTypes.number,
      weekendCapWorkload: PropTypes.number,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      deletedAt: PropTypes.string,
    })
  ),
  haveGoals: PropTypes.bool.isRequired,
};

export default compose(withConfig, withOffset)(WeeklyStackedBarChart);
